// Pure registration validation utilities isolated from any UI/React dependencies

export interface ValidationErrors {
    email: string[];
    tenant: string[];
    first_name: string[];
    last_name: string[];
    password: string[];
    repeatPassword: string[];
    locale: string[];
}

export interface RegistrationFormData {
    email: string;
    tenant: string;
    first_name: string;
    last_name: string;
    password: string;
    repeatPassword: string;
    hp: string; // honeypot
    locale: string;
}

// Factory to create a fresh, fully-typed empty errors object (no shared array references)
export const EMPTY_ERRORS = (): ValidationErrors => ({
    email: [],
    tenant: [],
    first_name: [],
    last_name: [],
    password: [],
    repeatPassword: [],
    locale: [],
});

// Pure validation function for isolated unit tests
export function validateRegistrationData(data: RegistrationFormData): ValidationErrors {
    const errors = EMPTY_ERRORS();

    const isEmailValid = /\S+@\S+\.\S+/.test(data.email);
    if (!isEmailValid) {
        errors.email.push('Invalid email address');
    }

    const pwdRule = (message: string, check: (password: string) => boolean): void => {
        if (!check(data.password)) {
            errors.password.push(message);
        }
    };

    pwdRule('Password must be at least 8 characters', p => p.length >= 8);
    pwdRule('Password must be maximum 30 characters', p => p.length <= 30);
    pwdRule('Password must contain at least one lowercase letter', p => /[a-z]/.test(p));
    pwdRule('Password must contain at least one uppercase letter', p => /[A-Z]/.test(p));
    pwdRule('Password must contain at least one digit', p => /[0-9]/.test(p));
    pwdRule('Password must contain at least one special character', p => /[!@#$%^&*()_=+\x5B\x5D{}|;:'",.<>?\x2F`~-]/.test(p));
    pwdRule('Password must not contain email prefix', p => {
        const emailPrefix = data.email.split('@')[0];
        return !p.toLowerCase().includes(emailPrefix.toLowerCase());
    });

    if (data.password !== data.repeatPassword) {
        errors.repeatPassword.push('Passwords do not match');
    }

    if (!data.tenant.trim()) {
        errors.tenant.push('Tenant is required');
    }
    if (!data.first_name.trim()) {
        errors.first_name.push('First name is required');
    }
    if (!data.last_name.trim()) {
        errors.last_name.push('Last name is required');
    }

    if (!data.locale) {
        errors.locale.push('Locale is required');
    }

    return errors;
}

// Re-export collection object for convenience (optional)
export const RegistrationValidation = {
    EMPTY_ERRORS,
    validateRegistrationData,
};

